import type { Feature, Polygon } from 'geojson';

/**
 * The client-side shape of a submission.
 *
 * The server deliberately does NOT import these types. It restates the contract
 * in `server/src/validation/payload.ts` as an independent check — a server that
 * shares the client's types is a server that trusts the client. If you change a
 * union here, change it there too; the server test suite is the backstop.
 */

export const SELECTION_METHODS = ['draw', 'manual'] as const;
export type SelectionMethod = (typeof SELECTION_METHODS)[number];

/**
 * Land classification, and the single largest price factor there is. Measured across
 * 21.212 scraped listings it accounts for 28,7% of all variation in price per square
 * metre on its own — more than county, more than size. Buildable land runs around
 * 62 EUR/m², extravilan around 12 and farmland around 6.
 *
 * Until this field existed the engine had to infer it from zoning and area, which put a
 * 1.300 m² farm plot in the buildable register and priced it ten times over. Optional
 * like the rest of step 2, with an explicit „nu știu” so an owner who genuinely does not
 * know is not pushed into a false answer; the inference still runs for those.
 *
 * Any value added here must also be accepted by the server allowlist AND the SQLite
 * CHECK constraint — see server/src/validation/payload.ts and storage/sqliteStore.ts.
 */
export const STATUT_TEREN_VALUES = ['intravilan', 'extravilan', 'agricol', 'nustiu'] as const;
export type StatutTeren = (typeof STATUT_TEREN_VALUES)[number];

export const ZONARE_VALUES = ['rezidential', 'mixt', 'comercial', 'altul', 'nustiu'] as const;
export type Zonare = (typeof ZONARE_VALUES)[number];

export const STARE_TEREN_VALUES = ['liber', 'constructie', 'partial'] as const;
export type StareTeren = (typeof STARE_TEREN_VALUES)[number];

/**
 * Legal status of the plot. Optional like the rest of step 2 — and note the
 * server allowlist AND the SQLite CHECK must both know any value added here.
 */
export const SITUATIE_JURIDICA_VALUES = [
  'unic',
  'coproprietari',
  'succesiune',
  'sarcini',
  'nustiu',
] as const;
export type SituatieJuridica = (typeof SITUATIE_JURIDICA_VALUES)[number];

/** Utilities available at (or near) the plot. Multi-select — see the server allowlist. */
export const UTILITATI_VALUES = ['curent', 'apa', 'gaz', 'canal'] as const;
export type Utilitate = (typeof UTILITATI_VALUES)[number];

/** Road access quality. Optional; the server allowlist AND SQLite CHECK must match. */
export const DRUM_VALUES = ['asfalt', 'pietruit', 'pamant'] as const;
export type Drum = (typeof DRUM_VALUES)[number];

export const FILE_KINDS = ['certificate', 'additional'] as const;
export type FileKind = (typeof FILE_KINDS)[number];

/** Accepted upload formats, enforced by extension here and by content sniff on the server. */
export const ACCEPTED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;
export const ACCEPTED_FILE_ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
export const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024;
export const MAX_FILE_SIZE_MB = 15;
export const MAX_ADDITIONAL_FILES = 10;

/** Step 1 output: where the parcel is and how big it is. */
export interface ParcelSelection {
  method: SelectionMethod;
  /** Present only when method === 'draw'. */
  geojson: Feature<Polygon> | null;
  /** m², from Turf for drawn parcels or typed by the owner for manual ones. */
  areaSqm: number | null;
  /** Centroid [lat, lng] of a drawn parcel, used to reverse-geocode the locality. */
  centroid: [number, number] | null;
  /** Locality guessed from the centroid, or typed in the manual fallback. */
  localityGuess: string;
}

/** Step 2 output. Numbers are held as strings so partial input stays editable. */
export interface ParcelDetails {
  areaSqm: string;
  judet: string;
  localitate: string;
  cut: string;
  pot: string;
  statutTeren: StatutTeren | '';
  zonare: Zonare | '';
  zonareAlt: string;
  stareTeren: StareTeren | '';
  situatieJuridica: SituatieJuridica | '';
  utilitati: Utilitate[];
  drum: Drum | '';
  intentieNote: string;
}

/** Step 3 output. */
export interface OwnerContact {
  name: string;
  email: string;
  phone: string;
  consent: boolean;
}

export interface SelectedFile {
  /** Stable key for React lists and removal — File objects are not identity-safe. */
  id: string;
  file: File;
}

export interface SubmissionFiles {
  certificate: SelectedFile | null;
  additional: SelectedFile[];
}

/** Everything the wizard collects, held in one place at the App level. */
export interface WizardState {
  selection: ParcelSelection;
  details: ParcelDetails;
  contact: OwnerContact;
  files: SubmissionFiles;
}

/** The JSON `payload` part of the multipart POST. Mirrored by the server schema. */
export interface SubmissionPayload {
  locale: string;
  selectionMethod: SelectionMethod;
  parcelGeojson: Feature<Polygon> | null;
  areaSqm: number;
  judet: string;
  localitate: string;
  cut: number;
  pot: number;
  statutTeren: StatutTeren | null;
  zonare: Zonare;
  zonareAlt: string | null;
  stareTeren: StareTeren;
  situatieJuridica: SituatieJuridica | null;
  utilitati: Utilitate[];
  drum: Drum | null;
  intentieNote: string;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  consentGiven: boolean;
  consentTimestamp: string;
  referrer: string | null;
}

export type SubmissionSuccess = { ok: true; id: string };

export type SubmissionErrorCode =
  | 'validation'
  | 'rate_limit'
  | 'too_large'
  | 'network'
  | 'server';

export interface SubmissionFailure {
  ok: false;
  code: SubmissionErrorCode;
  /** Field-keyed messages from the server, when it rejects specific fields. */
  fields?: Record<string, string>;
}

export type SubmissionResult = SubmissionSuccess | SubmissionFailure;
